import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { products } from './products';
import { config } from './config';

/* ============================================================
   HYPERCORE — monochrome rebuild, v3
   Black canvas, white ink, drifting light bleeds.
   Prices and stock come from the backend; the local catalogue
   is the fallback when the server can't be reached.
   ============================================================ */

const TIER_LABEL = {
  budget: 'Budget',
  performance: 'Performance',
  mainstream: 'Mainstream',
  premium: 'Premium',
  va: 'VA',
  oled: 'OLED',
  ddr4: 'DDR4',
  ddr5: 'DDR5'
};

const CATEGORIES = Object.entries(products).map(([key, group]) => {
  const items = Object.entries(group)
    .filter(([k]) => k !== 'title')
    .flatMap(([tier, arr]) =>
      (Array.isArray(arr) ? arr : []).map((p) => ({
        ...p,
        tier,
        tierLabel: TIER_LABEL[tier] || tier.toUpperCase(),
        category: key,
        categoryTitle: group.title
      }))
    );
  return { key, title: group.title, items };
});

const ALL_ITEMS = CATEGORIES.flatMap((c) => c.items);

const SYMBOL = { USD: '$', EUR: '\u20AC' };

function stockLevel(supply) {
  if (supply >= 30) return 4;
  if (supply >= 15) return 3;
  if (supply >= 6) return 2;
  if (supply >= 1) return 1;
  return 0;
}

const EMPTY_SHIPPING = { name: '', address: '', city: '', postal: '', country: '', phone: '' };

/* ---------- small building blocks ---------- */

function Meter({ supply }) {
  const lit = stockLevel(supply);
  return (
    <span className="meter" title={supply > 0 ? `${supply} in stock` : 'Out of stock'}>
      <span className="meter-bars" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <i key={i} className={i < lit ? 'on' : ''} />
        ))}
      </span>
      <span className="meter-num">{supply > 0 ? supply : 'out'}</span>
    </span>
  );
}

function Stars({ rating }) {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span className="stars" aria-label={`${r} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={i <= r ? 'on' : ''} aria-hidden="true">
          &#9733;
        </i>
      ))}
    </span>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="starpick" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          className={i <= value ? 'on' : ''}
          onClick={() => onChange(i)}
          role="radio"
          aria-checked={i === value}
          aria-label={`${i} star${i > 1 ? 's' : ''}`}
        >
          &#9733;
        </button>
      ))}
    </div>
  );
}

function SpecTable({ specs }) {
  const entries = Object.entries(specs || {});
  if (!entries.length) return <p className="muted">No spec sheet on file for this part.</p>;
  return (
    <dl className="spec">
      {entries.map(([k, v]) => (
        <div className="spec-row" key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ---------- ambient light bleeds (the signature) ---------- */

function Bleeds() {
  return (
    <div className="bleeds" aria-hidden="true">
      <span className="bleed b1" />
      <span className="bleed b2" />
      <span className="bleed b3" />
      <span className="bleed b4" />
      <span className="grain" />
    </div>
  );
}

/* ============================================================
   APP
   ============================================================ */

export default function App() {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('hypercore_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [currency, setCurrency] = useState(() => {
    try {
      return localStorage.getItem('hypercore_currency') || config.defaultCurrency || 'USD';
    } catch {
      return 'USD';
    }
  });

  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('hypercore_auth');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [shipping, setShipping] = useState(() => {
    try {
      const raw = localStorage.getItem('hypercore_shipping');
      return raw ? { ...EMPTY_SHIPPING, ...JSON.parse(raw) } : { ...EMPTY_SHIPPING };
    } catch {
      return { ...EMPTY_SHIPPING };
    }
  });

  /* live catalogue from the backend: { id: {priceUSD, priceEUR, supply} } */
  const [live, setLive] = useState(null);

  const [view, setView] = useState('shop');
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [payError, setPayError] = useState('');

  const [reviews, setReviews] = useState([]);
  const [reviewsState, setReviewsState] = useState('idle');
  const searchRef = useRef(null);

  /* ---- persistence ---- */
  useEffect(() => {
    try {
      localStorage.setItem('hypercore_cart', JSON.stringify(cart));
    } catch {
      /* storage unavailable — cart stays in memory this session */
    }
  }, [cart]);

  useEffect(() => {
    try {
      localStorage.setItem('hypercore_currency', currency);
    } catch {
      /* ignore */
    }
  }, [currency]);

  useEffect(() => {
    try {
      localStorage.setItem('hypercore_shipping', JSON.stringify(shipping));
    } catch {
      /* ignore */
    }
  }, [shipping]);

  /* ---- live catalogue: prices + stock from the backend, 5-min cache ---- */
  const loadCatalog = useCallback(async (force = false) => {
    const KEY = 'hypercore_catalog';
    const TTL = 5 * 60 * 1000;

    if (!force) {
      try {
        const raw = localStorage.getItem(KEY);
        if (raw) {
          const { data, timestamp } = JSON.parse(raw);
          if (Date.now() - timestamp < TTL && data) {
            setLive(data);
            return;
          }
        }
      } catch {
        /* fall through to network */
      }
    }

    try {
      const res = await fetch(`${config.apiUrl}/api/products`);
      const j = await res.json();
      if (j && j.success && Array.isArray(j.products)) {
        const map = {};
        for (const p of j.products) {
          map[p.id] = { priceUSD: p.priceUSD, priceEUR: p.priceEUR, supply: p.supply };
        }
        setLive(map);
        try {
          localStorage.setItem(KEY, JSON.stringify({ data: map, timestamp: Date.now() }));
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* server unreachable — the static catalogue keeps the shop browsable */
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  /* merge a product with its live price/stock when the backend has spoken */
  const resolve = useCallback(
    (p) => {
      const l = live && live[p.id];
      return l ? { ...p, priceUSD: l.priceUSD, priceEUR: l.priceEUR, supply: l.supply } : p;
    },
    [live]
  );

  const priceOf = useCallback(
    (line) => {
      const l = live && live[line.id];
      const src = l || line;
      return currency === 'EUR' ? src.priceEUR : src.priceUSD;
    },
    [live, currency]
  );

  /* ---- auth ---- */
  const handleAuthed = useCallback((payload) => {
    setUser(payload);
    try {
      localStorage.setItem('hypercore_auth', JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }, []);

  const handleLogout = useCallback(() => {
    setUser((current) => {
      if (current) {
        fetch(`${config.apiUrl}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + current.token }
        }).catch(() => {});
      }
      return null;
    });
    try {
      localStorage.removeItem('hypercore_auth');
    } catch {
      /* ignore */
    }
  }, []);

  /* ---- reviews: 1h cache, 30min refresh ---- */
  const loadReviews = useCallback(async (silent = false) => {
    const CACHE = 'hypercore_reviews_cache';
    const HOUR = 60 * 60 * 1000;

    if (!silent) {
      try {
        const raw = localStorage.getItem(CACHE);
        if (raw) {
          const { reviews: cached, timestamp } = JSON.parse(raw);
          if (Date.now() - timestamp < HOUR && Array.isArray(cached)) {
            setReviews(cached);
            setReviewsState('ready');
            return;
          }
        }
      } catch {
        /* fall through to network */
      }
      setReviewsState('loading');
    }

    try {
      const res = await fetch(`${config.apiUrl}/api/reviews`);
      const data = await res.json();
      if (data && data.success && Array.isArray(data.reviews)) {
        setReviews(data.reviews);
        setReviewsState('ready');
        try {
          localStorage.setItem(CACHE, JSON.stringify({ reviews: data.reviews, timestamp: Date.now() }));
        } catch {
          /* ignore */
        }
      } else {
        throw new Error('unexpected payload');
      }
    } catch {
      let recovered = false;
      try {
        const raw = localStorage.getItem(CACHE);
        if (raw) {
          const { reviews: cached } = JSON.parse(raw);
          if (Array.isArray(cached) && cached.length) {
            setReviews(cached);
            setReviewsState('ready');
            recovered = true;
          }
        }
      } catch {
        /* ignore */
      }
      if (!recovered) setReviewsState('error');
    }
  }, []);

  useEffect(() => {
    if (view !== 'feedback') return;
    loadReviews();
    const id = setInterval(() => loadReviews(true), 1800000);
    return () => clearInterval(id);
  }, [view, loadReviews]);

  /* ---- scroll lock for overlays ---- */
  const overlayOpen = menuOpen || cartOpen || !!detail || checkout;
  useEffect(() => {
    document.body.style.overflow = overlayOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [overlayOpen]);

  /* ---- escape closes topmost overlay ---- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (checkout) setCheckout(false);
      else if (detail) setDetail(null);
      else if (cartOpen) setCartOpen(false);
      else if (menuOpen) setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [checkout, detail, cartOpen, menuOpen]);

  /* ---- cart ops ---- */
  const addToCart = useCallback(
    (item) => {
      const l = live && live[item.id];
      const supply = l ? l.supply : item.supply;
      setCart((prev) => {
        const found = prev.find((x) => x.id === item.id);
        if (found) {
          if (Number.isFinite(supply) && found.qty >= supply) return prev; // can't take more than exists
          return prev.map((x) => (x.id === item.id ? { ...x, qty: x.qty + 1 } : x));
        }
        if (Number.isFinite(supply) && supply <= 0) return prev;
        return [
          ...prev,
          {
            id: item.id,
            name: item.name,
            priceUSD: item.priceUSD,
            priceEUR: item.priceEUR,
            imageUrl: item.imageUrl,
            qty: 1
          }
        ];
      });
    },
    [live]
  );

  const setQty = useCallback(
    (id, delta) => {
      setCart((prev) =>
        prev
          .map((l) => {
            if (l.id !== id) return l;
            let next = l.qty + delta;
            const lv = live && live[id];
            if (delta > 0 && lv && Number.isFinite(lv.supply)) {
              next = Math.min(next, Math.max(lv.supply, 1));
            }
            return { ...l, qty: Math.max(0, next) };
          })
          .filter((l) => l.qty > 0)
      );
    },
    [live]
  );

  const removeLine = useCallback((id) => setCart((prev) => prev.filter((l) => l.id !== id)), []);

  const count = cart.reduce((n, l) => n + l.qty, 0);
  const total = cart.reduce((s, l) => s + priceOf(l) * l.qty, 0);
  const totalLabel = SYMBOL[currency] + total.toFixed(2);

  /* ---- search ---- */
  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q) return null;
    return ALL_ITEMS.filter((p) => {
      if (p.name.toLowerCase().includes(q)) return true;
      if (p.categoryTitle.toLowerCase().includes(q)) return true;
      return Object.values(p.specs || {}).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [q]);

  const go = (next) => {
    setView(next);
    setMenuOpen(false);
    setCartOpen(false);
    setQuery('');
    setSearchOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const openCheckout = () => {
    setPlaced(false);
    setPayError('');
    setCartOpen(false);
    setCheckout(true);
  };

  /* ---- place the order: real request, server decides prices and stock ---- */
  const placeOrder = async () => {
    const missing = ['address', 'city', 'postal', 'country'].filter((k) => !String(shipping[k] || '').trim());
    if (missing.length) {
      setPayError('Fill in the delivery address first \u2014 street, city, postal code and country.');
      return;
    }
    setPayError('');
    setPlacing(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + user.token
        },
        body: JSON.stringify({
          currency,
          items: cart.map((l) => ({ id: l.id, qty: l.qty })),
          shipping: {
            name: shipping.name || user.name,
            address: shipping.address,
            city: shipping.city,
            postal: shipping.postal,
            country: shipping.country,
            phone: shipping.phone || ''
          }
        })
      });

      if (res.status === 401) {
        handleLogout();
        setPayError('Your session expired \u2014 sign in again from the menu to finish checkout.');
        setPlacing(false);
        return;
      }

      const data = await res.json();
      if (res.ok && data.success) {
        setPlaced(true);
        setCart([]);
        try {
          localStorage.removeItem('hypercore_catalog');
        } catch {
          /* ignore */
        }
        loadCatalog(true); // stock meters update with what's really left
      } else {
        setPayError(data.error || 'Couldn\u2019t place the order. Nothing was charged.');
        if (res.status === 409) {
          try {
            localStorage.removeItem('hypercore_catalog');
          } catch {
            /* ignore */
          }
          loadCatalog(true);
        }
      }
    } catch {
      setPayError('Can\u2019t reach the server \u2014 nothing was charged. Try again in a moment.');
    }
    setPlacing(false);
  };

  const setShip = (key) => (e) => setShipping((s) => ({ ...s, [key]: e.target.value }));

  const MENU = [
    ['shop', 'Parts', 'Browse the full catalogue'],
    ['feedback', 'Reviews', 'What buyers said after shipping'],
    ['account', user ? 'Account' : 'Sign in', user ? `Signed in as ${user.name}` : 'Orders and your own reviews'],
    ['contact', 'Contact', 'Reach a person, three ways'],
    ['terms', 'Terms', 'Pricing, shipping, returns, warranty']
  ];

  const d = detail ? resolve(detail) : null;

  return (
    <div className="app">
      <Bleeds />

      {/* ---------------- HEADER ---------------- */}
      <header className="hdr">
        <div className="hdr-in">
          <button className="wordmark" onClick={() => go('shop')} aria-label="HyperCore home">
            HYPERCORE
          </button>

          {view === 'shop' && (
            <div className={`search ${searchOpen ? 'open' : ''}`}>
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search parts, brands, specs"
                aria-label="Search parts"
              />
              {query && (
                <button className="search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                  &times;
                </button>
              )}
            </div>
          )}

          <div className="hdr-tools">
            {view === 'shop' && (
              <button
                className="icon-btn only-mobile"
                onClick={() => {
                  setSearchOpen((s) => !s);
                  setTimeout(() => searchRef.current && searchRef.current.focus(), 60);
                }}
                aria-label="Search"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
                </svg>
              </button>
            )}

            <div className="fx" role="group" aria-label="Currency">
              {['USD', 'EUR'].map((c) => (
                <button
                  key={c}
                  className={currency === c ? 'on' : ''}
                  onClick={() => setCurrency(c)}
                  aria-pressed={currency === c}
                >
                  {c}
                </button>
              ))}
            </div>

            <button className="icon-btn cart-btn no-mobile" onClick={() => setCartOpen(true)} aria-label={`Cart, ${count} items`}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M4 5h2l2.2 10.2a1.5 1.5 0 001.5 1.2h7.6a1.5 1.5 0 001.5-1.2L20.5 8H7" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="10" cy="20" r="1.2" fill="currentColor" stroke="none" />
                <circle cx="18" cy="20" r="1.2" fill="currentColor" stroke="none" />
              </svg>
              {count > 0 && <span className="pip">{count}</span>}
            </button>

            <button className="icon-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
              <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* ---------------- MAIN ---------------- */}
      <main className="main">
        {view === 'shop' && (
          <Shop
            results={results}
            query={query}
            currency={currency}
            resolve={resolve}
            onAdd={addToCart}
            onOpen={setDetail}
            onClear={() => setQuery('')}
          />
        )}
        {view === 'feedback' && <Feedback reviews={reviews} state={reviewsState} onRetry={() => loadReviews()} />}
        {view === 'account' && (
          <Account user={user} onAuthed={handleAuthed} onLogout={handleLogout} onSeeReviews={() => go('feedback')} />
        )}
        {view === 'contact' && <Contact />}
        {view === 'terms' && <Terms />}
      </main>

      {/* ---------------- FOOTER ---------------- */}
      <footer className="ftr">
        <div className="ftr-in">
          <div className="ftr-mark">HYPERCORE</div>
          <nav className="ftr-nav">
            <button onClick={() => go('shop')}>Parts</button>
            <button onClick={() => go('feedback')}>Reviews</button>
            <button onClick={() => go('account')}>{user ? 'Account' : 'Sign in'}</button>
            <button onClick={() => go('contact')}>Contact</button>
            <button onClick={() => go('terms')}>Terms</button>
          </nav>
          <p className="ftr-note">
            {ALL_ITEMS.length} parts in the catalogue. Prices shown in {currency}.
          </p>
        </div>
      </footer>

      {/* mobile-only sticky cart bar */}
      {count > 0 && !overlayOpen && (
        <button className="dock only-mobile" onClick={() => setCartOpen(true)}>
          <span className="dock-l">
            {count} {count === 1 ? 'part' : 'parts'}
          </span>
          <span className="dock-r">
            {totalLabel} <em>View cart</em>
          </span>
        </button>
      )}

      {/* ---------------- MENU ---------------- */}
      {menuOpen && (
        <div className="veil" onClick={() => setMenuOpen(false)}>
          <aside className="sheet sheet-menu" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Menu">
            <div className="sheet-top">
              <span className="eyebrow">Menu</span>
              <button className="icon-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                &times;
              </button>
            </div>
            <nav className="menu-nav">
              {MENU.map(([key, label, hint]) => (
                <button key={key} className={view === key ? 'on' : ''} onClick={() => go(key)}>
                  <span className="menu-label">{label}</span>
                  <span className="menu-hint">{hint}</span>
                </button>
              ))}
            </nav>
            <button className="btn btn-ghost full only-mobile" onClick={() => { setMenuOpen(false); setCartOpen(true); }}>
              Cart ({count})
            </button>
          </aside>
        </div>
      )}

      {/* ---------------- CART ---------------- */}
      {cartOpen && (
        <div className="veil" onClick={() => setCartOpen(false)}>
          <aside className="sheet sheet-cart" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Cart">
            <div className="sheet-top">
              <span className="eyebrow">Cart &mdash; {count} {count === 1 ? 'part' : 'parts'}</span>
              <button className="icon-btn" onClick={() => setCartOpen(false)} aria-label="Close cart">
                &times;
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="blank">
                <p>Cart&rsquo;s empty.</p>
                <button className="btn full" onClick={() => go('shop')}>
                  Browse parts
                </button>
              </div>
            ) : (
              <>
                <ul className="lines">
                  {cart.map((l) => (
                    <li key={l.id}>
                      <div className="line-head">
                        <span className="line-name">{l.name}</span>
                        <button className="line-x" onClick={() => removeLine(l.id)} aria-label={`Remove ${l.name}`}>
                          &times;
                        </button>
                      </div>
                      <div className="line-foot">
                        <div className="qty">
                          <button onClick={() => setQty(l.id, -1)} aria-label="Decrease quantity">
                            &minus;
                          </button>
                          <span>{l.qty}</span>
                          <button onClick={() => setQty(l.id, 1)} aria-label="Increase quantity">
                            +
                          </button>
                        </div>
                        <span className="line-sum">
                          {SYMBOL[currency]}
                          {(priceOf(l) * l.qty).toFixed(2)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="sheet-foot">
                  <div className="sum-row">
                    <span>Subtotal</span>
                    <span className="sum-total">{totalLabel}</span>
                  </div>
                  <p className="muted tiny">Shipping and duties are calculated at checkout.</p>
                  <button className="btn full" onClick={openCheckout}>
                    Checkout
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      )}

      {/* ---------------- PRODUCT DETAIL ---------------- */}
      {d && (
        <div className="veil veil-center" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={d.name}>
            <button className="modal-x" onClick={() => setDetail(null)} aria-label="Close">
              &times;
            </button>

            <div className="modal-grid">
              <div className="modal-shot">
                {d.imageUrl ? (
                  <img src={d.imageUrl} alt={d.name} loading="lazy" />
                ) : (
                  <span className="shot-fallback">{d.categoryTitle}</span>
                )}
              </div>

              <div className="modal-body">
                <span className="eyebrow">
                  {d.categoryTitle} &middot; {d.tierLabel}
                </span>
                <h2 className="modal-name">{d.name}</h2>
                <div className="modal-price">{SYMBOL[currency]}{Number(currency === 'EUR' ? d.priceEUR : d.priceUSD).toFixed(2)}</div>
                <Meter supply={d.supply} />
                <SpecTable specs={d.specs} />
                <button
                  className="btn full"
                  disabled={d.supply <= 0}
                  onClick={() => {
                    addToCart(d);
                    setDetail(null);
                    setCartOpen(true);
                  }}
                >
                  {d.supply <= 0 ? 'Out of stock' : 'Add to cart'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- CHECKOUT ---------------- */}
      {checkout && (
        <div className="veil veil-center" onClick={() => setCheckout(false)}>
          <div className="modal modal-pay" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Checkout">
            <button className="modal-x" onClick={() => setCheckout(false)} aria-label="Close">
              &times;
            </button>

            {!user ? (
              /* checkout requires an account: we need to know whose door to ship to */
              <div className="gate">
                <span className="eyebrow">Checkout</span>
                <h2 className="modal-name">Sign in to check out</h2>
                <p className="muted">
                  Orders ship to a named account &mdash; that&rsquo;s how your delivery address and order history stay
                  attached to you. Your cart is saved and will be waiting.
                </p>
                <div className="ok-row">
                  <button className="btn" onClick={() => { setCheckout(false); go('account'); }}>
                    Go to sign in
                  </button>
                  <button className="btn btn-ghost" onClick={() => setCheckout(false)}>
                    Keep browsing
                  </button>
                </div>
              </div>
            ) : placed ? (
              <div className="pay-done">
                <span className="eyebrow">Order placed</span>
                <h2 className="modal-name">Thanks &mdash; that&rsquo;s in.</h2>
                <p className="muted">
                  It ships to {shipping.address}, {shipping.city}. A confirmation is on its way to {user.email}, and the
                  order now sits in your account history &mdash; every part in it can be reviewed from there.
                </p>
                <button className="btn full" onClick={() => { setCheckout(false); go('shop'); }}>
                  Keep browsing
                </button>
              </div>
            ) : (
              <>
                <span className="eyebrow">Checkout</span>
                <h2 className="modal-name">
                  {count} {count === 1 ? 'part' : 'parts'} &middot; {totalLabel}
                </h2>

                <div className="ship">
                  <h3 className="ship-h">Where should it ship?</h3>
                  <div className="field">
                    <label htmlFor="sh-name">Recipient</label>
                    <input id="sh-name" type="text" value={shipping.name} onChange={setShip('name')} placeholder={user.name} autoComplete="name" />
                  </div>
                  <div className="field">
                    <label htmlFor="sh-addr">Street address</label>
                    <input id="sh-addr" type="text" value={shipping.address} onChange={setShip('address')} autoComplete="street-address" required />
                  </div>
                  <div className="ship-grid">
                    <div className="field">
                      <label htmlFor="sh-city">City</label>
                      <input id="sh-city" type="text" value={shipping.city} onChange={setShip('city')} autoComplete="address-level2" required />
                    </div>
                    <div className="field">
                      <label htmlFor="sh-postal">Postal code</label>
                      <input id="sh-postal" type="text" value={shipping.postal} onChange={setShip('postal')} autoComplete="postal-code" required />
                    </div>
                  </div>
                  <div className="ship-grid">
                    <div className="field">
                      <label htmlFor="sh-country">Country</label>
                      <input id="sh-country" type="text" value={shipping.country} onChange={setShip('country')} autoComplete="country-name" required />
                    </div>
                    <div className="field">
                      <label htmlFor="sh-phone">Phone (optional)</label>
                      <input id="sh-phone" type="tel" value={shipping.phone} onChange={setShip('phone')} autoComplete="tel" />
                    </div>
                  </div>
                  <p className="muted tiny">Saved in your browser for next time, and attached to this order.</p>
                </div>

                <p className="muted pay-lead">Choose how you&rsquo;d like to pay.</p>
                <div className="pay-grid">
                  {['Visa', 'Mastercard', 'PayPal', 'Apple Pay', 'Google Pay', 'Bank transfer'].map((m) => (
                    <button key={m} className="pay-tile" onClick={placeOrder} disabled={placing}>
                      {m}
                    </button>
                  ))}
                </div>
                {placing && <p className="muted tiny pulse">Placing your order&hellip;</p>}
                {payError && <p className="form-err">{payError}</p>}
              </>
            )}
          </div>
        </div>
      )}

      <style>{CSS}</style>
    </div>
  );
}

/* ============================================================
   VIEWS
   ============================================================ */

function Shop({ results, query, currency, resolve, onAdd, onOpen, onClear }) {
  return (
    <>
      {!results && (
        <section className="hero">
          <span className="eyebrow">PC components &middot; ships worldwide</span>
          <h1 className="hero-h">
            Every spec,
            <br />
            up front.
          </h1>
          <p className="hero-p">
            {ALL_ITEMS.length} parts across {CATEGORIES.length} categories. Full spec sheets on every card, stock counts
            straight from the warehouse ledger, and no bundles you didn&rsquo;t ask for.
          </p>
          <div className="hero-stats">
            {CATEGORIES.map((c) => (
              <a key={c.key} href={`#cat-${c.key}`} className="stat">
                <span className="stat-n">{String(c.items.length).padStart(2, '0')}</span>
                <span className="stat-l">{c.title}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      {results ? (
        <section className="cat">
          <div className="cat-top">
            <h2 className="cat-h">Search</h2>
            <span className="cat-n">
              {results.length} {results.length === 1 ? 'match' : 'matches'} for &ldquo;{query}&rdquo;
            </span>
          </div>
          {results.length === 0 ? (
            <div className="blank">
              <p>Nothing matches &ldquo;{query}&rdquo;.</p>
              <p className="muted tiny">Try a model number, a brand, or a spec like &ldquo;16GB&rdquo;.</p>
              <button className="btn" onClick={onClear}>
                Clear search
              </button>
            </div>
          ) : (
            <div className="grid">
              {results.map((p) => (
                <Card key={p.id} p={p} currency={currency} resolve={resolve} onAdd={onAdd} onOpen={onOpen} />
              ))}
            </div>
          )}
        </section>
      ) : (
        CATEGORIES.map((c) => (
          <section className="cat" id={`cat-${c.key}`} key={c.key}>
            <div className="cat-top">
              <h2 className="cat-h">{c.title}</h2>
              <span className="cat-n">{String(c.items.length).padStart(2, '0')} parts</span>
            </div>
            <div className="grid">
              {c.items.map((p) => (
                <Card key={p.id} p={p} currency={currency} resolve={resolve} onAdd={onAdd} onOpen={onOpen} />
              ))}
            </div>
          </section>
        ))
      )}
    </>
  );
}

function Card({ p, currency, resolve, onAdd, onOpen }) {
  const [open, setOpen] = useState(false);
  const item = resolve(p);
  const out = item.supply <= 0;
  return (
    <article className={`card ${out ? 'card-out' : ''}`}>
      <button className="card-shot" onClick={() => onOpen(p)} aria-label={`View ${item.name}`}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} loading="lazy" />
        ) : (
          <span className="shot-fallback">{item.categoryTitle}</span>
        )}
        <span className="card-tier">{item.tierLabel}</span>
      </button>

      <div className="card-body">
        <h3 className="card-name" onClick={() => onOpen(p)}>
          {item.name}
        </h3>

        <div className="card-row">
          <span className="card-price">
            {SYMBOL[currency]}
            {Number(currency === 'EUR' ? item.priceEUR : item.priceUSD).toFixed(2)}
          </span>
          <Meter supply={item.supply} />
        </div>

        <button className="disc" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <span>Spec sheet</span>
          <i className={open ? 'up' : ''} aria-hidden="true" />
        </button>
        {open && (
          <div className="disc-body">
            <SpecTable specs={item.specs} />
          </div>
        )}

        <button className="btn full" onClick={() => onAdd(item)} disabled={out}>
          {out ? 'Out of stock' : 'Add to cart'}
        </button>
      </div>
    </article>
  );
}

function Feedback({ reviews, state, onRetry }) {
  const avg = reviews.length ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length : 0;

  return (
    <section className="page">
      <span className="eyebrow">Reviews</span>
      <h1 className="page-h">What buyers said</h1>

      {state === 'loading' && <p className="muted pulse">Loading reviews&hellip;</p>}

      {state === 'error' && (
        <div className="blank">
          <p>Couldn&rsquo;t reach the review server.</p>
          <p className="muted tiny">The store still works &mdash; only reviews are unavailable.</p>
          <button className="btn" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      {state === 'ready' && reviews.length === 0 && (
        <div className="blank">
          <p>No reviews yet.</p>
          <p className="muted tiny">The first one lands here once an order ships.</p>
        </div>
      )}

      {state === 'ready' && reviews.length > 0 && (
        <>
          <div className="score">
            <span className="score-n">{avg.toFixed(1)}</span>
            <div className="score-meta">
              <Stars rating={Math.round(avg)} />
              <span className="muted tiny">
                {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
              </span>
            </div>
          </div>

          <ul className="reviews">
            {reviews.map((r, i) => (
              <li className="review" key={r.id || i}>
                <div className="review-top">
                  <div>
                    <div className="review-who">
                      {r.userName}
                      {r.verified && <span className="verified">Verified buyer</span>}
                    </div>
                    <div className="muted tiny">
                      {r.date ? new Date(r.date).toLocaleDateString() : ''}
                    </div>
                  </div>
                  <Stars rating={r.rating} />
                </div>
                {r.productName && (
                  <div className="review-part">
                    <span>{r.productName}</span>
                    {r.price != null && (
                      <span className="review-paid">
                        {r.currency || ''}
                        {Number(r.price).toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
                <p className="review-text">{r.review}</p>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/* ---------- account: sign in, orders, write a review ---------- */

function Account({ user, onAuthed, onLogout, onSeeReviews }) {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [orders, setOrders] = useState(null);
  const [ordersError, setOrdersError] = useState(false);

  const [pick, setPick] = useState(0);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setOrders(null);
    setOrdersError(false);
    (async () => {
      try {
        const res = await fetch(`${config.apiUrl}/api/orders`, {
          headers: { Authorization: 'Bearer ' + user.token }
        });
        if (res.status === 401) {
          onLogout();
          return;
        }
        const data = await res.json();
        if (!alive) return;
        if (data.success && Array.isArray(data.orders)) setOrders(data.orders);
        else setOrdersError(true);
      } catch {
        if (alive) setOrdersError(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, onLogout]);

  const purchases = useMemo(() => {
    if (!orders) return [];
    const seen = new Set();
    const out = [];
    for (const o of orders) {
      for (const it of o.items || []) {
        if (seen.has(it.name)) continue;
        seen.add(it.name);
        const eur = o.currency === 'EUR';
        out.push({
          name: it.name,
          symbol: eur ? '\u20AC' : '$',
          price: Number(eur ? it.priceEUR : it.priceUSD) || 0
        });
      }
    }
    return out;
  }, [orders]);

  const submitAuth = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const path = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login' ? { email, password } : { name, email, password };
      const res = await fetch(config.apiUrl + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        onAuthed({ token: data.token, name: data.user.name, email: data.user.email });
        setPassword('');
      } else {
        setError(data.error || 'That didn\u2019t work \u2014 check the details and try again.');
      }
    } catch {
      setError('Can\u2019t reach the server right now. Try again in a minute.');
    }
    setBusy(false);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    const p = purchases[pick];
    if (!p) return;
    if (text.trim().length < 10) {
      setSendError('Say a little more \u2014 at least 10 characters.');
      return;
    }
    setSendError('');
    setSending(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/reviews/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + user.token
        },
        body: JSON.stringify({
          productName: p.name,
          rating,
          review: text.trim(),
          price: p.price,
          currency: p.symbol
        })
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const data = await res.json();
      if (data.success) {
        setSent(true);
        setText('');
        try {
          localStorage.removeItem('hypercore_reviews_cache');
        } catch {
          /* ignore */
        }
      } else {
        setSendError(data.error || 'Couldn\u2019t save the review.');
      }
    } catch {
      setSendError('Can\u2019t reach the server right now.');
    }
    setSending(false);
  };

  /* ---- signed out ---- */
  if (!user) {
    return (
      <section className="page">
        <span className="eyebrow">Account</span>
        <h1 className="page-h">{mode === 'login' ? 'Sign in' : 'Create account'}</h1>
        <p className="page-p">
          Checkout needs an account &mdash; it&rsquo;s how your delivery address and order history stay attached to you.
          Every part you buy also unlocks a review slot.
        </p>

        <div className="fx auth-switch" role="group" aria-label="Sign in or create account">
          <button className={mode === 'login' ? 'on' : ''} onClick={() => { setMode('login'); setError(''); }}>
            Sign in
          </button>
          <button className={mode === 'register' ? 'on' : ''} onClick={() => { setMode('register'); setError(''); }}>
            Create account
          </button>
        </div>

        <form className="form" onSubmit={submitAuth}>
          {mode === 'register' && (
            <div className="field">
              <label htmlFor="acc-name">Name</label>
              <input
                id="acc-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </div>
          )}
          <div className="field">
            <label htmlFor="acc-email">Email</label>
            <input
              id="acc-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="acc-pass">Password</label>
            <input
              id="acc-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              minLength={8}
              required
            />
            {mode === 'register' && <span className="field-hint">At least 8 characters.</span>}
          </div>

          {error && <p className="form-err">{error}</p>}

          <button className="btn" disabled={busy}>
            {busy ? 'One moment\u2026' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>
      </section>
    );
  }

  /* ---- signed in ---- */
  return (
    <section className="page">
      <span className="eyebrow">Account</span>
      <h1 className="page-h">Hey, {user.name.split(' ')[0]}.</h1>
      <p className="page-p">{user.email}</p>

      <div className="acct-block">
        <h2 className="acct-h">Order history</h2>

        {ordersError && (
          <p className="muted">Couldn&rsquo;t load your orders right now. They&rsquo;re safe &mdash; try again in a minute.</p>
        )}
        {!ordersError && orders === null && <p className="muted pulse">Loading orders&hellip;</p>}
        {!ordersError && orders && orders.length === 0 && (
          <p className="muted">No orders on this account yet. Anything you buy from now on lands here.</p>
        )}
        {!ordersError && orders && orders.length > 0 && (
          <ul className="orders">
            {orders.map((o) => (
              <li className="order" key={o.id}>
                <div className="order-top">
                  <span className="order-id">#{String(o.id || '').slice(0, 6)}</span>
                  <span className="order-date">{o.date ? new Date(o.date).toLocaleDateString() : ''}</span>
                  <span className="order-total">
                    {o.currency === 'EUR' ? '\u20AC' : '$'}
                    {Number(o.total).toFixed(2)}
                  </span>
                </div>
                <ul className="order-items">
                  {(o.items || []).map((it, i) => (
                    <li key={i}>
                      <span className="oi-qty">{it.qty}&times;</span> {it.name}
                    </li>
                  ))}
                </ul>
                {o.shipping && (o.shipping.city || o.shipping.country) && (
                  <div className="order-ship">
                    &rarr; {[o.shipping.address, o.shipping.city, o.shipping.country].filter(Boolean).join(', ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="acct-block">
        <h2 className="acct-h">Write a review</h2>

        {purchases.length === 0 ? (
          <p className="muted">Reviews unlock after your first order &mdash; buy a part and it appears here.</p>
        ) : sent ? (
          <div className="form-ok">
            <span>Thanks &mdash; your review is live.</span>
            <div className="ok-row">
              <button type="button" className="btn" onClick={onSeeReviews}>
                See it on the reviews page
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setSent(false)}>
                Write another
              </button>
            </div>
          </div>
        ) : (
          <form className="form" onSubmit={submitReview}>
            <div className="field">
              <label htmlFor="rev-part">Part</label>
              <select id="rev-part" value={pick} onChange={(e) => setPick(Number(e.target.value))}>
                {purchases.map((p, i) => (
                  <option key={p.name} value={i}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Rating</label>
              <StarPicker value={rating} onChange={setRating} />
            </div>

            <div className="field">
              <label htmlFor="rev-text">Your review</label>
              <textarea
                id="rev-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="How has it held up? What should the next buyer know?"
                maxLength={1000}
              />
              <span className="field-hint">{text.length}/1000</span>
            </div>

            {sendError && <p className="form-err">{sendError}</p>}

            <button className="btn" disabled={sending}>
              {sending ? 'Posting\u2026' : 'Post review'}
            </button>
          </form>
        )}
      </div>

      <button className="btn btn-ghost sign-out" onClick={onLogout}>
        Sign out
      </button>
    </section>
  );
}

function Contact() {
  const channels = [
    {
      label: 'Discord',
      href: config.contact.discord,
      value: 'Join the server',
      hint: 'Build help and compatibility checks. Fastest reply.'
    },
    {
      label: 'Facebook',
      href: config.contact.facebook,
      value: 'Follow the page',
      hint: 'Restock notices and price drops.'
    },
    {
      label: 'Email',
      href: `mailto:${config.contact.email}`,
      value: config.contact.email,
      hint: 'Order issues, returns, invoices. Replies within a day.'
    }
  ];

  return (
    <section className="page">
      <span className="eyebrow">Contact</span>
      <h1 className="page-h">Three ways to reach us</h1>
      <p className="page-p">Pick whichever suits the question. A person reads all three.</p>

      <div className="channels">
        {channels.map((c) => (
          <a className="channel" key={c.label} href={c.href} target="_blank" rel="noopener noreferrer">
            <span className="channel-label">{c.label}</span>
            <span className="channel-value">{c.value}</span>
            <span className="channel-hint">{c.hint}</span>
            <span className="channel-go" aria-hidden="true">
              &rarr;
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function Terms() {
  const sections = [
    {
      h: 'Prices and currency',
      p: 'Prices are shown in USD or EUR using the switch in the header. All prices are set on our servers; the amount confirmed at checkout is the amount that applies, and it is verified server-side when the order is placed.'
    },
    {
      h: 'Stock',
      p: 'Stock counts come from a live warehouse ledger and go down the moment an order is placed. If an item sells out while it sits in your cart, checkout will say so before anything is charged.'
    },
    {
      h: 'Accounts and checkout',
      p: 'Placing an order requires an account with a delivery address, so every shipment has a named recipient and a door to go to. Browsing and the cart work without one.'
    },
    {
      h: 'Orders and payment',
      p: 'An order is confirmed once payment clears. We may cancel and refund an order if a listing carried a clear pricing error.'
    },
    {
      h: 'Reviews',
      p: 'Signed-in customers can publish a review for any part they bought; these carry a Verified buyer mark. Reviews must describe your own experience; we remove ones that don\u2019t.'
    },
    {
      h: 'Shipping',
      p: 'Orders leave the warehouse within two business days to the address given at checkout. Delivery times depend on destination and carrier. Import duties, where they apply, are the buyer\u2019s responsibility.'
    },
    {
      h: 'Returns',
      p: 'Unopened parts can be returned within 30 days of delivery for a full refund. Opened parts can be returned within 14 days if they are undamaged and complete. Return shipping is on the buyer unless the part arrived faulty.'
    },
    {
      h: 'Warranty',
      p: 'Every part carries the manufacturer warranty stated on its spec sheet. Faults inside the first 30 days are handled by us directly; after that we help you file with the manufacturer.'
    },
    {
      h: 'Your data',
      p: 'We store what an order needs: your name, email, delivery address, and order history. Passwords are stored hashed, never in plain text. We do not sell your data. Your cart is kept in your own browser, not on our servers.'
    }
  ];

  return (
    <section className="page">
      <span className="eyebrow">Terms</span>
      <h1 className="page-h">Terms of sale</h1>
      <p className="page-p">Plain summary of how buying here works.</p>

      <div className="terms">
        {sections.map((s, i) => (
          <div className="term" key={s.h}>
            <span className="term-n">{String(i + 1).padStart(2, '0')}</span>
            <div>
              <h3 className="term-h">{s.h}</h3>
              <p className="term-p">{s.p}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   STYLES
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Inter+Tight:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

:root{
  --void:#000;
  --surface:#0a0a0b;
  --raise:#111112;
  --edge:rgba(255,255,255,.10);
  --edge-hi:rgba(255,255,255,.30);
  --ink:#fafaf9;
  --ash:#8d8d8b;
  --dim:#5a5a5b;
  --display:'Syne',sans-serif;
  --body:'Inter Tight',system-ui,sans-serif;
  --mono:'JetBrains Mono',ui-monospace,monospace;
  --pad:clamp(16px,4vw,64px);
}

*,*::before,*::after{box-sizing:border-box;}

html,body,#root{background:var(--void);}
body{margin:0;}

.app{
  position:relative;
  min-height:100vh;
  background:var(--void);
  color:var(--ink);
  font-family:var(--body);
  font-size:15px;
  line-height:1.55;
  -webkit-font-smoothing:antialiased;
  overflow-x:hidden;
  display:flex;
  flex-direction:column;
}

button{font:inherit;color:inherit;background:none;border:none;cursor:pointer;}
a{color:inherit;text-decoration:none;}
img{max-width:100%;display:block;}

:focus-visible{outline:2px solid var(--ink);outline-offset:3px;border-radius:2px;}

/* ---------- signature: drifting light bleeds ---------- */
.bleeds{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden;}
.bleed{
  position:absolute;border-radius:50%;
  background:radial-gradient(circle at 50% 50%,rgba(255,255,255,.13),rgba(255,255,255,.05) 45%,transparent 70%);
  filter:blur(70px);
  will-change:transform;
}
.b1{width:52vw;height:52vw;top:-14vw;left:-10vw;animation:drift1 54s ease-in-out infinite;}
.b2{width:38vw;height:38vw;top:36vh;right:-12vw;opacity:.75;animation:drift2 71s ease-in-out infinite;}
.b3{width:44vw;height:44vw;top:118vh;left:18vw;opacity:.55;animation:drift3 63s ease-in-out infinite;}
.b4{width:30vw;height:30vw;top:196vh;right:6vw;opacity:.5;animation:drift1 82s ease-in-out infinite reverse;}

@keyframes drift1{0%,100%{transform:translate3d(0,0,0) scale(1);}50%{transform:translate3d(7vw,5vh,0) scale(1.12);}}
@keyframes drift2{0%,100%{transform:translate3d(0,0,0) scale(1.05);}50%{transform:translate3d(-8vw,-6vh,0) scale(.9);}}
@keyframes drift3{0%,100%{transform:translate3d(0,0,0) scale(.95);}50%{transform:translate3d(5vw,-7vh,0) scale(1.15);}}

.grain{
  position:absolute;inset:0;opacity:.5;mix-blend-mode:overlay;
  background-image:radial-gradient(rgba(255,255,255,.16) .5px,transparent .5px);
  background-size:3px 3px;
}

@media (prefers-reduced-motion:reduce){
  .bleed{animation:none !important;}
  *{transition-duration:.01ms !important;}
}

/* ---------- header ---------- */
.hdr{
  position:sticky;top:0;z-index:60;
  background:rgba(0,0,0,.72);
  backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);
  border-bottom:1px solid var(--edge);
}
.hdr-in{
  display:flex;align-items:center;gap:14px;
  padding:14px var(--pad);
  max-width:1400px;margin:0 auto;
}
.wordmark{
  font-family:var(--display);font-weight:800;
  font-size:clamp(15px,2.4vw,19px);
  letter-spacing:.16em;white-space:nowrap;
  padding:2px 0;
}
.hdr-tools{display:flex;align-items:center;gap:8px;margin-left:auto;}

.search{position:relative;flex:1;max-width:400px;margin-left:12px;}
.search input{
  width:100%;padding:9px 34px 9px 14px;
  background:var(--surface);
  border:1px solid var(--edge);border-radius:999px;
  color:var(--ink);font-family:var(--body);font-size:14px;
  transition:border-color .2s,background .2s;
}
.search input::placeholder{color:var(--dim);}
.search input:focus{outline:none;border-color:var(--edge-hi);background:var(--raise);}
.search-clear{position:absolute;right:10px;top:50%;transform:translateY(-50%);color:var(--ash);font-size:18px;line-height:1;}

.icon-btn{
  display:grid;place-items:center;position:relative;
  width:36px;height:36px;border-radius:999px;
  border:1px solid var(--edge);color:var(--ink);
  transition:border-color .2s,background .2s;
}
.icon-btn:hover{border-color:var(--edge-hi);background:var(--surface);}
.pip{
  position:absolute;top:-4px;right:-4px;
  min-width:17px;height:17px;padding:0 4px;
  display:grid;place-items:center;border-radius:999px;
  background:var(--ink);color:#000;
  font-family:var(--mono);font-size:10px;font-weight:500;
}

.fx{display:flex;border:1px solid var(--edge);border-radius:999px;overflow:hidden;width:max-content;}
.fx button{
  padding:7px 11px;font-family:var(--mono);font-size:11px;
  letter-spacing:.06em;text-indent:.06em;color:var(--dim);
  transition:color .2s,background .2s;
}
.fx button.on{background:var(--ink);color:#000;}

/* ---------- layout ---------- */
.main{position:relative;z-index:1;flex:1;max-width:1400px;width:100%;margin:0 auto;padding:0 var(--pad);}

.eyebrow{
  display:block;font-family:var(--mono);font-size:11px;
  letter-spacing:.22em;text-transform:uppercase;color:var(--ash);
}

.muted{color:var(--ash);}
.tiny{font-size:13px;}
.pulse{animation:pulse 1.6s ease-in-out infinite;}
@keyframes pulse{0%,100%{opacity:.4;}50%{opacity:1;}}

/* ---------- hero ---------- */
.hero{padding:clamp(56px,12vh,140px) 0 clamp(40px,7vh,80px);max-width:900px;}
.hero-h{
  font-family:var(--display);font-weight:800;
  font-size:clamp(40px,9vw,104px);
  line-height:.92;letter-spacing:-.035em;
  margin:18px 0 0;
}
.hero-p{max-width:52ch;color:var(--ash);font-size:clamp(15px,2vw,18px);margin:22px 0 0;}
.hero-stats{display:flex;flex-wrap:wrap;gap:10px;margin-top:36px;}
.stat{
  display:flex;align-items:baseline;gap:8px;
  padding:9px 14px;border:1px solid var(--edge);border-radius:999px;
  transition:border-color .2s,background .2s;
}
.stat:hover{border-color:var(--edge-hi);background:var(--surface);}
.stat-n{font-family:var(--mono);font-size:12px;color:var(--ink);}
.stat-l{font-size:13px;color:var(--ash);}

/* ---------- category ---------- */
.cat{padding:clamp(32px,6vh,64px) 0;}
.cat-top{
  display:flex;align-items:baseline;justify-content:space-between;gap:16px;
  padding-bottom:16px;margin-bottom:26px;border-bottom:1px solid var(--edge);
}
.cat-h{
  font-family:var(--display);font-weight:700;
  font-size:clamp(21px,3.4vw,34px);letter-spacing:-.02em;margin:0;
}
.cat-n{font-family:var(--mono);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--ash);white-space:nowrap;}

/* fluid grid: fills any resolution, from ultrawide to tablet */
.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:18px;}

/* ---------- card ---------- */
.card{
  position:relative;display:flex;flex-direction:column;
  background:var(--surface);
  border:1px solid var(--edge);border-radius:14px;
  overflow:hidden;transition:border-color .3s,transform .3s;
}
.card::after{
  content:'';position:absolute;inset:-30%;pointer-events:none;
  background:radial-gradient(circle at 50% 120%,rgba(255,255,255,.16),transparent 62%);
  opacity:0;transition:opacity .4s;
}
.card:hover{border-color:var(--edge-hi);transform:translateY(-3px);}
.card:hover::after{opacity:1;}
.card>*{position:relative;z-index:1;}

.card-shot{
  position:relative;width:100%;aspect-ratio:4/3;
  display:grid;place-items:center;padding:18px;
  background:#050505;border-bottom:1px solid var(--edge);
}
/* images sleep in grayscale, wake to colour on hover */
.card-shot img{
  max-height:100%;object-fit:contain;
  filter:grayscale(1) contrast(1.06);
  transition:filter .45s ease,transform .35s ease;
}
.card:hover .card-shot img{filter:none;transform:scale(1.03);}
.card-out .card-shot img{opacity:.45;}
.shot-fallback{font-family:var(--mono);font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--dim);}
.card-tier{
  position:absolute;top:10px;left:10px;
  padding:3px 8px;border:1px solid var(--edge);border-radius:999px;
  background:rgba(0,0,0,.6);backdrop-filter:blur(6px);
  font-family:var(--mono);font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--ash);
  text-align:center;text-indent:.14em; /* balances trailing letter-space so DDR4/DDR5 sit dead centre */
}

.card-body{display:flex;flex-direction:column;gap:12px;padding:16px;flex:1;}
.card-name{
  font-size:15px;font-weight:600;line-height:1.35;margin:0;cursor:pointer;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;
}
.card-row{display:flex;align-items:center;justify-content:space-between;gap:12px;}
.card-price{font-family:var(--mono);font-size:19px;font-weight:500;letter-spacing:-.02em;}

.meter{display:inline-flex;align-items:center;gap:7px;}
.meter-bars{display:inline-flex;gap:2px;}
.meter-bars i{width:4px;height:12px;border-radius:1px;background:rgba(255,255,255,.14);}
.meter-bars i.on{background:var(--ink);}
.meter-num{font-family:var(--mono);font-size:11px;color:var(--ash);}

.disc{
  display:flex;align-items:center;justify-content:space-between;width:100%;
  padding:9px 12px;border:1px solid var(--edge);border-radius:9px;
  font-family:var(--mono);font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--ash);
  transition:border-color .2s,color .2s;
}
.disc:hover{border-color:var(--edge-hi);color:var(--ink);}
.disc i{width:7px;height:7px;border-right:1.5px solid currentColor;border-bottom:1.5px solid currentColor;transform:rotate(45deg) translate(-2px,-2px);transition:transform .25s;}
.disc i.up{transform:rotate(-135deg) translate(-2px,-2px);}
.disc-body{border:1px solid var(--edge);border-radius:9px;padding:12px;background:#050505;}

.spec{margin:0;display:flex;flex-direction:column;gap:7px;}
.spec-row{display:grid;grid-template-columns:minmax(90px,38%) 1fr;gap:12px;align-items:baseline;}
.spec dt{font-size:12px;color:var(--dim);}
.spec dd{margin:0;font-family:var(--mono);font-size:12px;color:var(--ink);word-break:break-word;}

/* ---------- buttons ---------- */
.btn{
  padding:11px 20px;border-radius:999px;
  background:var(--ink);color:#000;
  font-size:14px;font-weight:600;letter-spacing:.01em;
  transition:opacity .2s,transform .12s;
}
.btn:hover{opacity:.86;}
.btn:active{transform:scale(.985);}
.btn:disabled{opacity:.35;cursor:not-allowed;}
.btn.full{width:100%;}
.btn-ghost{background:none;color:var(--ink);border:1px solid var(--edge);}
.btn-ghost:hover{border-color:var(--edge-hi);opacity:1;}
.card .btn{margin-top:auto;}

/* ---------- overlays ---------- */
.veil{
  position:fixed;inset:0;z-index:100;
  background:rgba(0,0,0,.7);
  backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);
  display:flex;justify-content:flex-end;
  animation:fade .2s ease;
}
.veil-center{align-items:center;justify-content:center;padding:var(--pad);}
@keyframes fade{from{opacity:0;}to{opacity:1;}}

.sheet{
  display:flex;flex-direction:column;
  width:min(420px,100%);height:100%;
  background:var(--surface);
  border-left:1px solid var(--edge);
  animation:slideX .28s cubic-bezier(.22,1,.36,1);
}
@keyframes slideX{from{transform:translateX(100%);}to{transform:translateX(0);}}
.sheet-top{
  display:flex;align-items:center;justify-content:space-between;gap:12px;
  padding:18px 20px;border-bottom:1px solid var(--edge);flex-shrink:0;
}
.sheet-top .icon-btn{font-size:20px;line-height:1;}

.menu-nav{display:flex;flex-direction:column;padding:8px 0;flex:1;overflow-y:auto;}
.menu-nav button{
  text-align:left;padding:16px 20px;border-bottom:1px solid var(--edge);
  display:flex;flex-direction:column;gap:3px;transition:background .2s;
}
.menu-nav button:hover{background:var(--raise);}
.menu-nav button.on .menu-label{text-decoration:underline;text-underline-offset:5px;}
.menu-label{font-family:var(--display);font-weight:700;font-size:20px;letter-spacing:-.01em;}
.menu-hint{font-size:13px;color:var(--ash);}
.sheet .btn.full{margin:16px 20px 20px;width:calc(100% - 40px);}

.lines{list-style:none;margin:0;padding:0;flex:1;overflow-y:auto;}
.lines li{padding:16px 20px;border-bottom:1px solid var(--edge);}
.line-head{display:flex;justify-content:space-between;gap:12px;}
.line-name{font-size:14px;font-weight:500;line-height:1.35;}
.line-x{color:var(--dim);font-size:19px;line-height:1;transition:color .2s;}
.line-x:hover{color:var(--ink);}
.line-foot{display:flex;align-items:center;justify-content:space-between;margin-top:12px;}
.qty{display:flex;align-items:center;border:1px solid var(--edge);border-radius:999px;overflow:hidden;}
.qty button{width:30px;height:28px;color:var(--ash);transition:color .2s,background .2s;}
.qty button:hover{color:var(--ink);background:var(--raise);}
.qty span{min-width:26px;text-align:center;font-family:var(--mono);font-size:12px;}
.line-sum{font-family:var(--mono);font-size:14px;}

.sheet-foot{padding:18px 20px 22px;border-top:1px solid var(--edge);flex-shrink:0;}
.sheet-foot .btn.full{margin:14px 0 0;width:100%;}
.sum-row{display:flex;justify-content:space-between;align-items:baseline;}
.sum-total{font-family:var(--mono);font-size:22px;}

.blank{display:flex;flex-direction:column;align-items:flex-start;gap:10px;padding:40px 20px;}
.blank .btn{margin-top:6px;}

/* ---------- modal ---------- */
.modal{
  position:relative;width:min(880px,100%);max-height:88vh;overflow-y:auto;
  background:var(--surface);border:1px solid var(--edge);border-radius:16px;
  padding:26px;animation:pop .26s cubic-bezier(.22,1,.36,1);
}
@keyframes pop{from{opacity:0;transform:translateY(14px) scale(.98);}to{opacity:1;transform:none;}}
.modal-x{position:absolute;top:14px;right:16px;z-index:2;font-size:24px;line-height:1;color:var(--ash);transition:color .2s;}
.modal-x:hover{color:var(--ink);}
.modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:26px;}
.modal-shot{
  display:grid;place-items:center;padding:22px;min-height:260px;
  background:#050505;border:1px solid var(--edge);border-radius:12px;
}
/* opened product shows its true colours */
.modal-shot img{max-height:340px;object-fit:contain;}
.modal-body{display:flex;flex-direction:column;gap:14px;}
.modal-name{font-family:var(--display);font-weight:700;font-size:clamp(20px,3vw,28px);line-height:1.15;letter-spacing:-.02em;margin:6px 0 0;}
.modal-price{font-family:var(--mono);font-size:28px;letter-spacing:-.02em;}
.modal-body .btn{margin-top:auto;}

.modal-pay{width:min(560px,100%);text-align:left;}
.pay-lead{margin:22px 0 0;}
.pay-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:14px;}
.pay-tile{
  padding:18px 8px;border:1px solid var(--edge);border-radius:11px;
  font-family:var(--mono);font-size:11px;letter-spacing:.06em;text-indent:.06em;text-align:center;color:var(--ash);
  transition:border-color .2s,color .2s,background .2s;
}
.pay-tile:hover:not(:disabled){border-color:var(--edge-hi);color:var(--ink);background:var(--raise);}
.pay-done{display:flex;flex-direction:column;gap:12px;}
.modal-pay .btn.full{margin-top:18px;}
.modal-pay .form-err{margin-top:14px;}
.modal-pay .muted.tiny{margin-top:12px;}

.gate{display:flex;flex-direction:column;gap:12px;}
.gate .ok-row{margin-top:8px;}

.ship{margin-top:22px;padding-top:20px;border-top:1px solid var(--edge);display:flex;flex-direction:column;gap:14px;}
.ship-h{font-family:var(--display);font-weight:700;font-size:17px;letter-spacing:-.01em;margin:0;}
.ship-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.ship .muted.tiny{margin:0;}

/* ---------- pages ---------- */
.page{padding:clamp(48px,10vh,110px) 0 clamp(48px,8vh,100px);max-width:840px;}
.page-h{font-family:var(--display);font-weight:800;font-size:clamp(32px,6.5vw,66px);line-height:1;letter-spacing:-.03em;margin:16px 0 0;}
.page-p{color:var(--ash);margin:18px 0 0;max-width:52ch;}

.channels{display:flex;flex-direction:column;gap:12px;margin-top:40px;}
.channel{
  position:relative;display:grid;gap:5px;
  padding:24px 56px 24px 24px;
  background:var(--surface);border:1px solid var(--edge);border-radius:14px;
  transition:border-color .25s,transform .25s,background .25s;
}
.channel:hover{border-color:var(--edge-hi);background:var(--raise);transform:translateX(4px);}
.channel-label{font-family:var(--display);font-weight:700;font-size:22px;letter-spacing:-.01em;}
.channel-value{font-family:var(--mono);font-size:12px;color:var(--ink);word-break:break-all;}
.channel-hint{font-size:13px;color:var(--ash);}
.channel-go{position:absolute;right:24px;top:50%;transform:translateY(-50%);font-size:20px;color:var(--dim);transition:color .25s,transform .25s;}
.channel:hover .channel-go{color:var(--ink);transform:translateY(-50%) translateX(4px);}

.terms{display:flex;flex-direction:column;gap:2px;margin-top:40px;}
.term{display:grid;grid-template-columns:52px 1fr;gap:16px;padding:22px 0;border-top:1px solid var(--edge);}
.term-n{font-family:var(--mono);font-size:11px;color:var(--dim);padding-top:4px;}
.term-h{font-size:16px;font-weight:600;margin:0 0 6px;}
.term-p{color:var(--ash);margin:0;font-size:14px;}

/* ---------- account ---------- */
.auth-switch{margin-top:34px;}
.form{display:flex;flex-direction:column;gap:16px;margin-top:24px;max-width:440px;}
.form .btn{align-self:flex-start;}
.field{display:flex;flex-direction:column;gap:6px;}
.field label{font-family:var(--mono);font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:var(--ash);}
.field input,.field textarea,.field select{
  width:100%;
  background:var(--surface);
  border:1px solid var(--edge);border-radius:10px;
  color:var(--ink);font-family:var(--body);font-size:14px;
  padding:11px 13px;
  transition:border-color .2s,background .2s;
}
.field textarea{min-height:110px;resize:vertical;line-height:1.5;}
.field select{appearance:none;-webkit-appearance:none;padding-right:34px;background-image:linear-gradient(45deg,transparent 50%,var(--ash) 50%),linear-gradient(135deg,var(--ash) 50%,transparent 50%);background-position:calc(100% - 19px) 50%,calc(100% - 14px) 50%;background-size:5px 5px;background-repeat:no-repeat;}
.field input:focus,.field textarea:focus,.field select:focus{outline:none;border-color:var(--edge-hi);background:var(--raise);}
.field-hint{font-family:var(--mono);font-size:10px;color:var(--dim);}
.form-err{
  font-size:13px;color:var(--ink);margin:0;
  background:var(--raise);border:1px solid var(--edge-hi);border-radius:9px;
  padding:10px 13px;
}
.form-ok{
  display:flex;flex-direction:column;gap:14px;align-items:flex-start;
  font-size:14px;color:var(--ink);
  background:var(--surface);border:1px solid var(--edge);border-radius:12px;
  padding:16px;max-width:440px;
}
.ok-row{display:flex;flex-wrap:wrap;gap:10px;}

.starpick{display:flex;gap:4px;}
.starpick button{font-size:24px;line-height:1;padding:2px;color:rgba(255,255,255,.18);transition:color .15s,transform .12s;}
.starpick button:hover{transform:scale(1.12);}
.starpick button.on{color:var(--ink);}

.acct-block{margin-top:44px;padding-top:28px;border-top:1px solid var(--edge);}
.acct-h{font-family:var(--display);font-weight:700;font-size:21px;letter-spacing:-.01em;margin:0 0 16px;}

.orders{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:10px;}
.order{background:var(--surface);border:1px solid var(--edge);border-radius:12px;padding:15px 16px;transition:border-color .25s;}
.order:hover{border-color:var(--edge-hi);}
.order-top{display:flex;align-items:baseline;gap:12px;font-family:var(--mono);font-size:12px;}
.order-id{color:var(--dim);}
.order-date{color:var(--ash);}
.order-total{margin-left:auto;color:var(--ink);font-size:14px;}
.order-items{list-style:none;margin:10px 0 0;padding:0;display:flex;flex-direction:column;gap:4px;font-size:13px;color:var(--ash);}
.oi-qty{font-family:var(--mono);color:var(--dim);margin-right:4px;}
.order-ship{margin-top:10px;font-family:var(--mono);font-size:11px;color:var(--dim);word-break:break-word;}

.sign-out{margin-top:40px;}

/* ---------- reviews ---------- */
.score{display:flex;align-items:center;gap:18px;margin-top:36px;padding-bottom:26px;border-bottom:1px solid var(--edge);}
.score-n{font-family:var(--display);font-weight:800;font-size:clamp(44px,9vw,76px);line-height:.9;letter-spacing:-.04em;}
.score-meta{display:flex;flex-direction:column;gap:5px;}
.stars{display:inline-flex;gap:2px;font-size:13px;}
.stars i{color:rgba(255,255,255,.16);font-style:normal;}
.stars i.on{color:var(--ink);}

.reviews{list-style:none;margin:26px 0 0;padding:0;display:flex;flex-direction:column;gap:12px;}
.review{background:var(--surface);border:1px solid var(--edge);border-radius:13px;padding:18px;transition:border-color .25s;}
.review:hover{border-color:var(--edge-hi);}
.review-top{display:flex;justify-content:space-between;align-items:flex-start;gap:14px;}
.review-who{font-weight:600;font-size:14px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.verified{
  font-family:var(--mono);font-size:9px;font-weight:400;
  letter-spacing:.12em;text-transform:uppercase;color:var(--ash);
  border:1px solid var(--edge);border-radius:999px;padding:2px 7px;
  text-indent:.12em;text-align:center;
}
.review-part{
  display:flex;justify-content:space-between;align-items:baseline;gap:14px;
  margin:12px 0;padding:9px 12px;border-radius:8px;background:#050505;
  font-family:var(--mono);font-size:11px;color:var(--ash);
}
.review-paid{color:var(--ink);white-space:nowrap;}
.review-text{margin:0;font-size:14px;color:var(--ink);}

/* ---------- footer ---------- */
.ftr{position:relative;z-index:1;border-top:1px solid var(--edge);margin-top:60px;}
.ftr-in{max-width:1400px;margin:0 auto;padding:40px var(--pad) 48px;display:flex;flex-direction:column;gap:18px;}
.ftr-mark{font-family:var(--display);font-weight:800;font-size:clamp(30px,8vw,64px);letter-spacing:.1em;color:rgba(255,255,255,.12);line-height:1;}
.ftr-nav{display:flex;flex-wrap:wrap;gap:20px;}
.ftr-nav button{font-size:14px;color:var(--ash);transition:color .2s;}
.ftr-nav button:hover{color:var(--ink);}
.ftr-note{margin:0;font-family:var(--mono);font-size:11px;color:var(--dim);}

/* ---------- mobile dock ---------- */
.dock{
  position:fixed;left:12px;right:12px;bottom:12px;z-index:80;
  display:flex;align-items:center;justify-content:space-between;gap:12px;
  padding:14px 18px;border-radius:999px;
  background:var(--ink);color:#000;
  box-shadow:0 8px 40px rgba(0,0,0,.6);
  animation:dockUp .3s cubic-bezier(.22,1,.36,1);
}
@keyframes dockUp{from{transform:translateY(90px);opacity:0;}to{transform:none;opacity:1;}}
.dock-l{font-family:var(--mono);font-size:12px;}
.dock-r{display:flex;align-items:center;gap:10px;font-family:var(--mono);font-size:13px;font-weight:500;}
.dock-r em{font-family:var(--body);font-style:normal;font-weight:600;font-size:13px;}

.only-mobile{display:none;}

/* ============================================================
   TABLET  (<= 1024px)
   ============================================================ */
@media (max-width:1024px){
  .modal-grid{grid-template-columns:1fr;gap:20px;}
  .modal-shot{min-height:200px;}
  .search{max-width:280px;}
}

/* ============================================================
   MOBILE  (<= 640px)
   ============================================================ */
@media (max-width:640px){
  .app{font-size:14px;}
  .only-mobile{display:flex;}
  .no-mobile{display:none;}

  /* header: search collapses behind an icon and drops to its own row */
  .hdr-in{padding:12px var(--pad);gap:10px;}
  .search{
    position:absolute;left:var(--pad);right:var(--pad);top:100%;
    max-width:none;margin:0;
    opacity:0;pointer-events:none;transform:translateY(-8px);
    transition:opacity .2s,transform .2s;
  }
  .search.open{
    opacity:1;pointer-events:auto;transform:none;
    padding-bottom:12px;background:rgba(0,0,0,.92);
    backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);
  }
  .search.open input{margin-top:2px;}
  .search-clear{top:calc(50% - 5px);}
  .fx button{padding:6px 9px;font-size:10px;}

  /* hero */
  .hero{padding:44px 0 32px;}
  .hero-stats{gap:8px;}
  .stat{padding:7px 12px;}

  /* one column, roomier cards */
  .grid{grid-template-columns:1fr;gap:14px;}
  .card-shot{aspect-ratio:16/10;}
  .card-name{-webkit-line-clamp:3;font-size:16px;}
  .card-price{font-size:22px;}
  .card-body{padding:16px;gap:14px;}
  .btn{padding:13px 20px;}

  /* no hover on touch: opening the product is the colour moment */

  /* specs stack instead of two columns */
  .spec-row{grid-template-columns:1fr;gap:2px;}
  .spec dt{font-size:11px;letter-spacing:.06em;text-transform:uppercase;}

  /* drawers become bottom sheets */
  .veil{align-items:flex-end;justify-content:stretch;}
  .sheet{
    width:100%;height:auto;max-height:86vh;
    border-left:none;border-top:1px solid var(--edge);
    border-radius:18px 18px 0 0;
    animation:slideY .3s cubic-bezier(.22,1,.36,1);
  }
  @keyframes slideY{from{transform:translateY(100%);}to{transform:translateY(0);}}
  .sheet-top{padding:16px 18px;}
  .menu-label{font-size:18px;}

  /* modals fill the screen */
  .veil-center{padding:0;align-items:flex-end;}
  .modal{
    width:100%;max-height:92vh;border-radius:18px 18px 0 0;
    padding:22px 18px 26px;animation:slideY .3s cubic-bezier(.22,1,.36,1);
  }
  .modal-shot{min-height:170px;padding:16px;}
  .modal-shot img{max-height:200px;}
  .pay-grid{grid-template-columns:repeat(2,1fr);}
  .ship-grid{grid-template-columns:1fr;}

  /* pages */
  .page{padding:40px 0 48px;}
  .channel{padding:20px 46px 20px 18px;}
  .channel-label{font-size:19px;}
  .channel-go{right:18px;}
  .term{grid-template-columns:38px 1fr;gap:12px;padding:18px 0;}

  /* account forms go full width, buttons full width */
  .form{max-width:none;}
  .form .btn{align-self:stretch;width:100%;}
  .form-ok{max-width:none;}
  .ok-row{width:100%;}
  .ok-row .btn{flex:1;}
  .order-top{flex-wrap:wrap;row-gap:2px;}
  .acct-block{margin-top:36px;padding-top:22px;}
  .sign-out{width:100%;}

  .score{gap:14px;margin-top:28px;}
  .review-part{flex-direction:column;align-items:flex-start;gap:4px;}

  .ftr{margin-top:40px;}
  .ftr-in{padding:32px var(--pad) 96px;}
  .ftr-nav{gap:16px;}

  /* leave room for the docked cart bar */
  .main{padding-bottom:8px;}
}

/* ============================================================
   SMALL PHONES  (<= 380px)
   ============================================================ */
@media (max-width:380px){
  .wordmark{font-size:14px;letter-spacing:.12em;}
  .hdr-tools{gap:6px;}
  .icon-btn{width:33px;height:33px;}
  .pay-grid{grid-template-columns:1fr;}
  .dock{padding:12px 15px;}
}
`;
